import React from 'react';
import POICarousel from '../components/POICarousel';
import POIPopup from '../components/POIPopup';
import { useState } from 'react';
import { POI, Card, poiService } from '../services/poiService';
import { deletePoi } from '../services/api';
import { cardCacheService } from '../services/cardCacheService';
import { useLanguage } from '../contexts/LanguageContext';

const DiscoverPage: React.FC = () => {
  const { selectedLanguage } = useLanguage();
  const [selectedPOI, setSelectedPOI] = useState<POI | null>(null);
  const [selectedPOICards, setSelectedPOICards] = useState<Card[]>([]);
  const [isLoadingCards, setIsLoadingCards] = useState(false);

  // Handle POI click to view details
  const handleViewDetails = async (poi: POI) => {
    setSelectedPOI(poi);
    
    // Check cache first
    const cachedCards = cardCacheService.getCachedCards(poi.id);
    if (cachedCards) {
      setSelectedPOICards(cachedCards);
      setIsLoadingCards(false);
      return;
    }

    // Check if already loading
    if (cardCacheService.isLoading(poi.id)) {
      setIsLoadingCards(true);
      return;
    }

    setIsLoadingCards(true);
    setSelectedPOICards([]);
    cardCacheService.setLoading(poi.id, true);

    try {
      const cardsResponse = await poiService.fetchCardsByPOI(poi.id, selectedLanguage?.code);
      const cards = cardsResponse.data;
      
      // Cache the results
      cardCacheService.setCachedCards(poi.id, cards);
      setSelectedPOICards(cards);
    } catch (err) {
      console.error(`Error fetching cards for POI ${poi.id}:`, err);
      setSelectedPOICards([]);
    } finally {
      setIsLoadingCards(false);
      cardCacheService.setLoading(poi.id, false);
    }
  };

  // Close POI popup
  const handleClosePopup = () => {
    setSelectedPOI(null);
    setSelectedPOICards([]);
    setIsLoadingCards(false);
  };

  // Handle POI delete (admin only)
  const handlePOIDelete = async (poiIdV2: string) => {
    try {
      await deletePoi(poiIdV2);

      // Clear the selected POI if it was the deleted one
      if (selectedPOI?.id === poiIdV2) {
        setSelectedPOI(null);
        setSelectedPOICards([]);
      }

      // Clear cache for this POI
      cardCacheService.clearCachedCards(poiIdV2);
    } catch (error) {
      console.error('Error deleting POI:', error);
      throw error;
    }
  };

  // Handle POI edit (admin only)
  const handlePOIEdit = (updatedPOI: POI) => {
    // Update the selected POI with the new data
    setSelectedPOI(updatedPOI);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-8">
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">
            Discover Random Places
          </h1>
          <p className="text-base sm:text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto px-2">
            Explore random points of interest from around the world.
          </p>
        </div>

        {/* POI Carousel */}
        <POICarousel onPOIClick={handleViewDetails} />
      </div>

      {/* POI Details Popup */}
      {selectedPOI && (
        <POIPopup
          poi={selectedPOI}
          cards={selectedPOICards}
          isLoading={isLoadingCards}
          onClose={handleClosePopup}
          onCardDelete={async () => {}} // Disable card deletion on discover page
          onCardEdit={async () => {}} // Disable card editing on discover page
          onPOIDelete={handlePOIDelete}
          onPOIEdit={handlePOIEdit}
        />
      )}
    </div>
  );
};

export default DiscoverPage;
